import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { FaEnvelope, FaArrowLeft, FaCheck } from 'react-icons/fa';
import toast from 'react-hot-toast';
import { authAPI } from '../../api/endpoints';
import LoadingSpinner from '../common/LoadingSpinner';
import './Auth.css';

const ForgotPassword = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [email, setEmail] = useState('');

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm();

  const onSubmit = async (data) => {
    setLoading(true);
    setEmail(data.email);

    try {
      const res = await authAPI.forgotPassword({ email: data.email });
      const payload = res?.data || {};

      // Handle 3 possible success shapes:
      //   1. { success: true, message: "..." }
      //   2. { data: { success: true, message: "..." } }
      //   3. { code: "check_email", ... }  (WP-typical)
      const isSuccess =
        payload.success === true ||
        payload.data?.success === true ||
        payload.code === 'check_email' ||
        payload.code === 'email_sent';

      if (isSuccess) {
        setSubmitted(true);
        toast.success('Password reset link sent to your email!');
      } else {
        toast.error(
          payload.message ||
          payload.data?.message ||
          payload.code ||
          'Failed to send reset link. Please try again.'
        );
      }
    } catch (err) {
      // Axios error — check response for a message
      const status = err?.response?.status;
      const message =
        err?.response?.data?.message ||
        err?.response?.data?.data?.message ||
        err?.message;

      if (status === 429) {
        toast.error('Too many requests. Please wait a minute and try again.');
      } else if (status === 404) {
        toast.error('Email not found. Please check and try again.');
      } else if (status === 500) {
        toast.error('Server could not send the email. Please try again later.');
      } else {
        toast.error(message || 'Network error. Please try again.');
      }
      console.error('Forgot password error:', err);
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="auth-page">
        <div className="auth-container">
          <div className="auth-card success-card">
            <div className="success-icon">
              <FaCheck />
            </div>
            <h2>Check Your Email</h2>
            <p>
              We've sent a password reset link to <strong>{email}</strong>
            </p>
            <p className="success-hint">
              Please check your inbox and follow the instructions to reset your
              password. If you don't see it, check your spam folder.
            </p>
            <div className="success-actions">
              <button
                onClick={() => navigate('/login')}
                className="btn btn-primary"
                type="button"
              >
                Back to Login
              </button>
              <button
                onClick={() => {
                  setSubmitted(false);
                  setEmail('');
                }}
                className="btn btn-secondary"
                type="button"
              >
                Try Again
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="auth-page">
      <div className="auth-container">
        <div className="auth-card">
          <div className="auth-header">
            <Link to="/login" className="back-to-login">
              <FaArrowLeft /> Back to Login
            </Link>
            <div className="auth-logo">
              <span className="logo-icon">🔐</span>
              <h1>Forgot Password</h1>
            </div>
            <p className="auth-subtitle">
              Enter your email address and we'll send you a link to reset your
              password.
            </p>
          </div>

          <form onSubmit={handleSubmit(onSubmit)} className="auth-form">
            <div className="form-group">
              <label>Email Address</label>
              <div className="input-wrapper">
                <FaEnvelope className="input-icon" />
                <input
                  type="email"
                  placeholder="Enter your registered email"
                  autoComplete="email"
                  autoFocus
                  {...register('email', {
                    required: 'Email is required',
                    pattern: {
                      value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                      message: 'Please enter a valid email',
                    },
                  })}
                />
              </div>
              {errors.email && (
                <span className="error-message">{errors.email.message}</span>
              )}
            </div>

            <div className="forgot-info">
              <p>We'll send a password reset link to your email address.</p>
              <p className="info-note">
                Make sure to check your spam folder if you don't see it.
              </p>
            </div>

            <button
              type="submit"
              className="auth-button"
              disabled={loading}
            >
              {loading ? <LoadingSpinner size="small" /> : 'Send Reset Link'}
            </button>

            <div className="auth-divider">
              <span>or</span>
            </div>

            <p className="auth-footer">
              Remember your password? <Link to="/login">Login</Link>
            </p>
          </form>
        </div>
      </div>
    </div>
  );
};

export default ForgotPassword;